import { DepthSeriesPoint } from './logTracks';
import { DepthExtremes } from './useSyncedDepthExtremes';

/**
 * The plot box every track shares.
 *
 * Built from the same constants `LogTrack` puts in each chart spec, not
 * measured from the DOM. That is deliberate: a measured `chart.plotTop` reads
 * 0 while the tab panel is `display: none` (the panel is `keepMounted`), and
 * jsdom measures nothing at all. A test asserts the spec's margins match these
 * numbers, so the two cannot drift apart.
 */
export interface PlotBox {
  top: number;
  height: number;
}

/**
 * Depth to a pixel offset within the body row.
 *
 * Exact only because the depth axis is pinned with `startOnTick: false` and
 * `endOnTick: false` -- otherwise Highcharts rounds the domain outward to a
 * tick and `axis.min` stops being the window we were handed.
 */
export const depthToOffset = (
  depth: number,
  extremes: DepthExtremes,
  plot: PlotBox
): number => {
  const span = extremes.max - extremes.min;
  if (!(span > 0)) return plot.top;
  return plot.top + ((depth - extremes.min) / span) * plot.height;
};

/** The inverse. Null in the margins above and below the plot. */
export const offsetToDepth = (
  offset: number,
  extremes: DepthExtremes,
  plot: PlotBox
): number | null => {
  if (!(plot.height > 0)) return null;
  const fraction = (offset - plot.top) / plot.height;
  if (fraction < 0 || fraction > 1) return null;
  return extremes.min + fraction * (extremes.max - extremes.min);
};

/**
 * The value of a curve at (or nearest to) a depth.
 *
 * Reads the React point arrays rather than `series.points`, which matters for
 * two independent reasons: Highcharts crops `series.points` to the visible
 * window past `cropThreshold` (300, and there are ~621 rows), so index *i* in
 * the data is not point *i*; and `boost` removes `series.points` entirely.
 */
export const valueAtDepth = (
  points: DepthSeriesPoint[],
  depth: number,
  tolerance: number
): number | null => {
  if (points.length === 0) return null;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (points[mid][0] < depth) low = mid + 1;
    else high = mid;
  }
  const candidates = low > 0 ? [low - 1, low] : [low];
  let best: DepthSeriesPoint | null = null;
  let bestDistance = Infinity;
  candidates.forEach(index => {
    const point = points[index];
    if (!point) return;
    const distance = Math.abs(point[0] - depth);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
    }
  });
  if (best === null || bestDistance > tolerance) return null;
  return (best as DepthSeriesPoint)[1];
};

export interface DepthCrosshairState {
  depth: number | null;
  /** Pointer position within the track row, for placing the tooltip. */
  x: number;
  y: number;
  /** The row's own size, so the tooltip can flip away from an edge. */
  /** Row-relative offset past which the tooltip would be clipped. */
  clipRight: number;
  clipBottom: number;
}

export interface DepthCrosshairPosition {
  depth: number | null;
  x: number;
  y: number;
  /** Row-relative offset past which the tooltip would be clipped. */
  clipRight: number;
  clipBottom: number;
}

export interface DepthCrosshairStore {
  subscribe: (listener: () => void) => () => void;
  /** Must be referentially stable between commits or useSyncExternalStore loops. */
  getSnapshot: () => DepthCrosshairState;
  set: (next: DepthCrosshairPosition) => void;
  clear: () => void;
  /** Runs any pending frame now. Test seam. */
  flush: () => void;
}

const NO_DEPTH: DepthCrosshairState = {
  depth: null,
  x: 0,
  y: 0,
  clipRight: 0,
  clipBottom: 0
};

/**
 * The hovered depth, held outside React state on purpose.
 *
 * A `useState` here would re-render `LogViewer` on every pointer move, which
 * rebuilds five chart specs and has `HighchartsReact` call `chart.update()` on
 * all of them — at pointer rate. Only leaf components subscribe, so a move
 * repaints the crosshair line and the readouts and nothing else.
 *
 * Created per viewer (a `useMemo`, not a module singleton): a module-level
 * store would survive between jest files in the same worker and make tests
 * order-dependent.
 */
export const createDepthCrosshairStore = (
  schedule: (callback: () => void) => number = requestAnimationFrame,
  cancel: (handle: number) => void = cancelAnimationFrame
): DepthCrosshairStore => {
  let state: DepthCrosshairState = NO_DEPTH;
  let pending: number | null = null;
  let next: DepthCrosshairPosition | null = null;
  const listeners = new Set<() => void>();

  const commit = () => {
    pending = null;
    if (next === null) {
      if (state.depth === null) return;
      state = NO_DEPTH;
    } else {
      if (
        state.depth === next.depth &&
        state.x === next.x &&
        state.y === next.y
      ) {
        return;
      }
      state = next.depth === null ? NO_DEPTH : { ...next, depth: next.depth };
    }
    listeners.forEach(listener => listener());
  };

  return {
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => state,
    set: position => {
      next = position;
      // Coalesce a burst of pointer events into one commit per frame.
      if (pending === null) pending = schedule(commit);
    },
    clear: () => {
      next = null;
      if (pending === null) pending = schedule(commit);
    },
    flush: () => {
      if (pending === null) return;
      cancel(pending);
      commit();
    }
  };
};
